// src/App.jsx
import React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MainPage from "./page/MainPage";
import RoomListPage from "./page/RoomListPage";
import ManagePage from "./page/management/ManagePage";
import FakePaymentPage from "./page/FakePaymentPage";
import CouponPage from "./page/CouponPage";
import StorePage from "./page/management/StorePage";
import RewardCardPage from "./page/RewardCardPage";
import ResetPasswordPage from "./page/ResetPasswordPage";
import ErrorBoundary from "./component/ErrorBoundary";
import TablePage from "./page/management/TablePage";
import PermissionPage from "./page/management/PermissionPage";
import "./css/App.css";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import UserPage from "./page/management/UserPage";
import OrderResultPage from "./page/OrderResultPage";
import MyOrderPage from "./page/MyOrderPage";
import CMSCouponPage from "./page/management/coupon/CouponPage";
import NoticePage from "./page/NoticePage";
import ProfilePage from "./page/ProfilePage";
import LoginPage from "./page/management/LoginPage";
import WebPage from "./page/management/WebPage";
import ReportPage from "./page/management/ReportPage";
import ReservationForm from "./component/ReservationForm";
import RegisterPage from "./page/RegisterPage";
import ForgotPasswordPage from "./page/ForgotPasswordPage";
import AIGolfPage from "./page/AIGolfPage";
import PromotionPage from "./page/PromotionPage";
import ChargeCoinPage from "./page/ChargeCoinPage";
import PaymentSuccessPage from "./page/PaymentSuccessPage";
import PayBridge from "./component/PayBridge";
import { AuthProvider } from "./context/AuthContext";
import VersionChecker from "./component/VersionChecker";

/* ===== Dark Theme ===== */
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#ffffff" }, // text/icons on dark
    secondary: { main: "#000000" }, // rarely used in dark
    info: { main: "#02a4e3" }, // brand highlight
    background: {
      default: "#0f1113", // page background
      paper: "#14181b", // cards, sheets
      secondary: "#1b2326", // your custom secondary bg
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b8bf",
      selected: "#ffffff",
    },
    divider: "rgba(255,255,255,0.12)",
    appbar: "#12161a", // custom app bar color
    selected: "#02a4e3", // custom selected color
    bonus: "#005580",
    chip: "#d89f6a",
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& label": {
            color: "rgba(255,255,255,0.7)",
          },
          "& label.Mui-focused": {
            color: "#7fb7ff",
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: "#7fb7ff",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "rgba(255,255,255,0.24)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(255,255,255,0.38)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#7fb7ff",
            },
            "& input": {
              fontWeight: "bold",
              fontSize: "1rem",
              color: "#e6eef7",
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, textTransform: "none" },
        containedInfo: {
          boxShadow: "0 2px 10px rgba(2,164,227,0.35)",
          "&:hover": { boxShadow: "0 4px 16px rgba(2,164,227,0.45)" },
        },
        outlinedInfo: {
          borderWidth: 2,
          "&:hover": { backgroundColor: "rgba(2,164,227,0.12)" },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#14181b",
          borderColor: "rgba(255,255,255,0.12)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#12161a",
        },
      },
    },
  },
});

const isManageSubdomain = window.location.hostname.startsWith("console.");

const userRoutes = createRoutesFromElements([
  <Route>
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/app/store/:storeId/promotion" element={<PromotionPage />} />
    <Route path="/app/store/:storeId" element={<AIGolfPage />} />
    <Route path="/app/store/:storeId/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/app/coupon" element={<CouponPage />} />
    <Route path="/app/rewardCard" element={<RewardCardPage />} />
    <Route path="/app/:storeId/rooms" element={<RoomListPage />} />
    <Route path="/app/:storeId/reserve" element={<ReservationForm />} />
    <Route path="/app/myOrder" element={<MyOrderPage />} />
    <Route path="/app/notice" element={<NoticePage />} />
    <Route path="/app/profile" element={<ProfilePage />} />
    <Route path="/app/:storeId/:userId/charge" element={<ChargeCoinPage />} />
    <Route
      path="/app/paymentSimulation/:orderId"
      element={<FakePaymentPage />}
    />
    <Route
      path="/app/order/orderResult/:orderId"
      element={<OrderResultPage />}
    />

    <Route path="/pay-bridge" element={<PayBridge />} />
  </Route>,
  <Route path="*" element={<Navigate to="/app" replace />} />,
]);

const adminRoutes = createRoutesFromElements([
  <Route path="/login" element={<LoginPage />} />,
  <Route path="/dashboard" element={<ManagePage />}>
    <Route path="web/setting" element={<WebPage />} />
    <Route path="store/new" element={<StorePage />} />
    <Route path="store/tables" element={<TablePage />} />
    <Route path="user/query" element={<UserPage />} />
    <Route path="coupon/new" element={<CMSCouponPage />} />
    <Route path="permission" element={<PermissionPage />} />
    <Route path="report" element={<ReportPage />} />
  </Route>,
  <Route errorElement={<ErrorBoundary />} />,
]);

const router = createBrowserRouter(isManageSubdomain ? adminRoutes : userRoutes);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <VersionChecker />
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
